import React from 'react';
import {alpha, darken} from '@mui/material/styles';
import {svgIconClasses} from '@mui/material/SvgIcon';
import {toggleButtonClasses} from '@mui/material/ToggleButton';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {green, gray, brand, blue, warningOrange} from '../themePrimitives';
import {bluePulse} from '../animations';

const dangerRed = '#d3302f';
const dangerRedHover = darken(dangerRed, 0.1);
const dangerRedActive = darken(dangerRed, 0.2);

const infoBlue = '#027bf3';
const infoBlueHover = darken(infoBlue, 0.1);
const infoBlueActive = darken(infoBlue, 0.2);
const segmentedControlShadow = '0 1px 2px rgba(30, 24, 20, 0.08), 0 3px 8px rgba(30, 24, 20, 0.08)';
const segmentedControlMaxItems = 12;
const segmentedControlItemCountStyles = (rootSelector, itemSelector) => Object.fromEntries(
    Array.from({length: segmentedControlMaxItems}, (_, index) => [
        `${rootSelector}:has(> ${itemSelector}:nth-of-type(${index + 1}):last-of-type)`,
        {'--segmented-control-item-width': `calc((100% - 8px - ${index * 4}px) / ${index + 1})`}
    ])
);
const segmentedControlSelectedIndexStyles = (rootSelector, itemSelector, selectedSelector) => Object.fromEntries(
    Array.from({length: segmentedControlMaxItems}, (_, index) => [
        `${rootSelector}:has(> ${itemSelector}:nth-of-type(${index + 1})${selectedSelector})`,
        {'--segmented-control-selected-offset': `calc(${index * 100}% + ${index * 4}px)`}
    ])
);
const segmentedControlIndicatorStyles = theme => ({
    content: '""',
    position: 'absolute',
    top: 4,
    bottom: 4,
    left: 4,
    zIndex: 0,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: (theme.vars || theme).palette.background.paper,
    boxShadow: segmentedControlShadow,
    pointerEvents: 'none',
    transition: 'transform 180ms ease, width 180ms ease',
    width: 'var(--segmented-control-item-width, calc(100% - 8px))',
    transform: 'translateX(var(--segmented-control-selected-offset, 0px))'
});
// import {outlinedInputClasses} from '@mui/material/OutlinedInput';

// ONE height for every control that can share a row.
//
// A filter bar routinely puts a segmented control, a picker and an action button
// side by side (the hub's requirement Members tab is the clearest case: status
// toggles, a Departments autocomplete and "Record completion"). These were set
// independently and landed on three different numbers — 40, 40 and ~42.5 — which
// reads as sloppy alignment rather than a deliberate size.
//
// So they derive from here instead. A ToggleButtonGroup is padding + button, so
// its button is sized to leave room for the group's own padding; everything else
// takes the height directly.
//
// The button's medium padding is deliberately 1px under what the label needs, so
// this height GOVERNS rather than acting as a floor the content overshoots by a
// fraction of a pixel.
const CONTROL_HEIGHT = 42;
const CONTROL_HEIGHT_MEDIUM = 54;
const TOGGLE_GROUP_PADDING = 4;
const TOGGLE_BUTTON_HEIGHT = CONTROL_HEIGHT - TOGGLE_GROUP_PADDING * 2;

// Where the RESTING (un-shrunk) outlined label sits, so it's centred in the
// field rather than riding high in it.
//
// MUI hard-codes this at 9px for small and 16px for medium — correct for ITS
// defaults, which are a 16px body font (a 23px label line) in a 40px/56px field:
// (40 - 23) / 2 ≈ 9. This theme has a 14px body font and its own control
// heights, so those constants centre nothing. Recompute from the two numbers it
// actually depends on instead of swapping in different magic ones.
const restingLabelOffset = (theme, height) => {
    const fontPx = parseFloat(theme.typography.body1?.fontSize) * 16 || 14;
    const labelLineHeight = fontPx * 1.4375; // MUI's FormLabel line-height
    return Math.round((height - labelLineHeight) / 2);
};

export const inputsCustomizations = {
    MuiButtonBase: {
        defaultProps: {
            disableTouchRipple: true,
            disableRipple: true
        },
        styleOverrides: {
            root: ({theme}) => ({
                boxSizing: 'border-box',
                transition: 'all 100ms ease-in',
                '&:focus-visible': {
                    outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    outlineOffset: '2px'
                }
                
                // '&.MuiIconButton-root': {
                //     width: 'auto'
                // }
            })
        }
    },
    MuiButton: {
        styleOverrides: {
            root: ({theme}) => ({
                boxShadow: 'none',
                borderRadius: (theme.vars || theme).shape.borderRadius,
                textTransform: 'none',
                // Reserve 1px on every button so contained variants without an explicit
                // border (e.g. secondary, warning) match the height of outlined and other
                // contained variants. box-sizing is border-box, so without this an
                // un-bordered button is 2px taller in content area than a bordered one.
                border: '1px solid transparent',
                '&.Mui-disabled': {
                    borderColor: 'transparent'
                },
                // `className="pulse"` composes with any color/variant to draw
                // attention to an in-progress state (active checks, etc).
                // Sets --pulse-color per palette color so the ring matches the
                // button's color rather than always being blue.
                '&.pulse': {
                    animation: `${bluePulse} 1.8s ease-in-out infinite`,
                    '--pulse-color-fade': 'transparent',
                    '&.MuiButton-colorPrimary': {'--pulse-color': alpha(theme.palette.primary.main, 0.5)},
                    '&.MuiButton-colorSecondary': {'--pulse-color': alpha(theme.palette.secondary.main, 0.5)},
                    // Info pulse keyed to blue[400] — the same value palette.info.main
                    // now resolves to, and what the info chip/button render as.
                    '&.MuiButton-colorInfo': {'--pulse-color': alpha(blue[400], 0.5)},
                    '&.MuiButton-colorSuccess': {'--pulse-color': alpha(theme.palette.success.main, 0.5)},
                    '&.MuiButton-colorWarning': {'--pulse-color': alpha(theme.palette.warning.main, 0.5)},
                    '&.MuiButton-colorError': {'--pulse-color': alpha(theme.palette.error.main, 0.5)}
                },
                variants: [
                    {
                        // Dashed "add / drop-zone" affordance — e.g. a full-width
                        // "Add step" control. Reads as a placeholder action rather
                        // than a solid primary button. Composes with `fullWidth`.
                        props: {
                            variant: 'dashed'
                        },
                        style: {
                            border: `1.5px dashed ${gray[400]}`,
                            backgroundColor: 'transparent',
                            color: (theme.vars || theme).palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                            padding: '12px 16px',
                            '&:hover': {
                                backgroundColor: gray[100],
                                borderColor: gray[500],
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: gray[200]
                            },
                            '&.Mui-disabled': {
                                color: (theme.vars || theme).palette.text.disabled,
                                border: `1.5px dashed ${gray[300]}`
                            }
                        }
                    },
                    {
                        props: {
                            color: 'primary',
                            variant: 'contained'
                        },
                        style: {
                            color: 'white',
                            backgroundColor: 'hsl(215, 12%, 18%)',
                            border: '1px solid hsl(215, 12%, 18%)',
                            '&:hover': {
                                backgroundColor: 'hsl(215, 13%, 14%)',
                                borderColor: 'hsl(215, 13%, 14%)',
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: 'hsl(216, 14%, 10%)',
                                borderColor: 'hsl(216, 14%, 10%)'
                            }
                        }
                    },
                    {
                        props: {
                            color: 'success',
                            variant: 'contained'
                        },
                        style: {
                            color: 'white',
                            backgroundColor: alpha(green[500], 0.9),
                            // backgroundImage: `linear-gradient(to bottom, ${alpha(brand[400], 0.8)}, ${brand[500]})`,
                            // boxShadow: `inset 0 2px 0 ${alpha(brand[200], 0.2)}, inset 0 -2px 0 ${alpha(brand[700], 0.4)}`,
                            border: `1px solid ${green[500]}`,
                            '&:hover': {
                                backgroundColor: green[600],
                                borderColor: green[700],
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: green[700],
                                backgroundImage: 'none'
                            }
                        }
                    },
                    {
                        props: {
                            color: 'danger',
                            variant: 'contained'
                        },
                        style: {
                            color: 'white',
                            backgroundColor: dangerRed,
                            border: `1px solid ${dangerRed}`,
                            '&:hover': {
                                backgroundColor: dangerRedHover,
                                borderColor: dangerRedHover,
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: dangerRedActive,
                                borderColor: dangerRedActive,
                                backgroundImage: 'none'
                            }
                        }
                    },
                    {
                        props: {
                            color: 'secondary',
                            variant: 'contained'
                        },
                        style: {
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: theme.typography.fontWeightMedium,
                            letterSpacing: 0,
                            color: (theme.vars || theme).palette.text.primary,
                            backgroundColor: gray[100],
                            '&:hover': {
                                backgroundColor: gray[200],
                                borderColor: gray[300],
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: gray[300],
                                boxShadow: 'none'
                            }
                        }
                    },
                    {
                        props: {
                            color: 'error',
                            variant: 'contained'
                        },
                        style: {
                            color: 'white',
                            backgroundColor: dangerRed,
                            border: `1px solid ${dangerRed}`,
                            '&:hover': {
                                backgroundColor: dangerRedHover,
                                borderColor: dangerRedHover,
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: dangerRedActive,
                                borderColor: dangerRedActive,
                                backgroundImage: 'none'
                            }
                        }
                    },
                    {
                        props: {
                            color: 'warning',
                            variant: 'contained'
                        },
                        style: {
                            color: warningOrange.onFill,
                            backgroundColor: warningOrange.fill,
                            border: `1px solid ${warningOrange.fill}`,
                            '&:hover': {
                                backgroundColor: warningOrange.fillHover,
                                borderColor: warningOrange.fillHover,
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: warningOrange.fillHover,
                                backgroundImage: 'none'
                            }
                        }
                    },
                    {
                        props: {
                            color: 'info',
                            variant: 'contained'
                        },
                        style: {
                            color: 'white',
                            backgroundColor: infoBlue,
                            border: `1px solid ${infoBlue}`,
                            '&:hover': {
                                backgroundColor: infoBlueHover,
                                borderColor: infoBlueHover,
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: infoBlueActive,
                                borderColor: infoBlueActive,
                                backgroundImage: 'none'
                            }
                        }
                    },
                    {
                        props: {
                            variant: 'outlined'
                        },
                        style: {
                            padding: '8px 12px',
                            color: (theme.vars || theme).palette.text.primary,
                            border: '1px solid',
                            borderColor: gray[200],
                            backgroundColor: alpha(gray[50], 0.3),
                            '&:hover': {
                                backgroundColor: gray[100],
                                borderColor: gray[300]
                            },
                            '&:active': {
                                backgroundColor: gray[200]
                            }
                        }
                    },
                    // Outlined variants below all follow the same pattern as danger/error/info:
                    // text color, hover borderColor, and alpha(tone, 0.4) base borderColor all
                    // derive from a single medium-dark "tone" per color. This keeps the hover
                    // transition subtle AND ensures the right border (which MUI's ButtonGroup
                    // forces to currentColor on hover for first+middle children) always matches
                    // the other three sides on the hovered button.
                    {
                        props: {
                            color: 'secondary',
                            variant: 'outlined'
                        },
                        style: {
                            color: brand[700],
                            border: '1px solid',
                            borderColor: alpha(gray[400], 0.45),
                            backgroundColor: alpha(gray[50], 0.6),
                            '&:hover': {
                                backgroundColor: gray[100],
                                borderColor: gray[400]
                            },
                            '&:active': {
                                backgroundColor: gray[200],
                                borderColor: gray[500]
                            }
                        }
                    },
                    {
                        props: {
                            color: 'primary',
                            variant: 'outlined'
                        },
                        style: {
                            color: gray[700],
                            border: '1px solid',
                            borderColor: alpha(gray[700], 0.4),
                            backgroundColor: alpha(gray[700], 0.05),
                            '&:hover': {
                                backgroundColor: alpha(gray[700], 0.1),
                                borderColor: gray[700]
                            },
                            '&:active': {
                                backgroundColor: alpha(gray[700], 0.18)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'success',
                            variant: 'outlined'
                        },
                        style: {
                            color: green[500],
                            border: '1px solid',
                            borderColor: alpha(green[500], 0.4),
                            backgroundColor: alpha(green[500], 0.05),
                            '&:hover': {
                                backgroundColor: alpha(green[500], 0.1),
                                borderColor: green[500]
                            },
                            '&:active': {
                                backgroundColor: alpha(green[500], 0.18)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'danger',
                            variant: 'outlined'
                        },
                        style: {
                            color: dangerRed,
                            border: '1px solid',
                            borderColor: alpha(dangerRed, 0.4),
                            backgroundColor: alpha(dangerRed, 0.05),
                            '&:hover': {
                                backgroundColor: alpha(dangerRed, 0.1),
                                borderColor: dangerRed
                            },
                            '&:active': {
                                backgroundColor: alpha(dangerRed, 0.18)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'error',
                            variant: 'outlined'
                        },
                        style: {
                            color: dangerRed,
                            border: '1px solid',
                            borderColor: alpha(dangerRed, 0.4),
                            backgroundColor: alpha(dangerRed, 0.05),
                            '&:hover': {
                                backgroundColor: alpha(dangerRed, 0.1),
                                borderColor: dangerRed
                            },
                            '&:active': {
                                backgroundColor: alpha(dangerRed, 0.18)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'info',
                            variant: 'outlined'
                        },
                        style: {
                            color: infoBlue,
                            border: '1px solid',
                            borderColor: alpha(infoBlue, 0.4),
                            backgroundColor: alpha(infoBlue, 0.05),
                            '&:hover': {
                                backgroundColor: alpha(infoBlue, 0.1),
                                borderColor: infoBlue
                            },
                            '&:active': {
                                backgroundColor: alpha(infoBlue, 0.18)
                            }
                        }
                    },
                    {
                        props: {
                            variant: 'text'
                        },
                        style: {
                            color: gray[600],
                            '&:hover': {
                                backgroundColor: gray[100]
                            },
                            '&:active': {
                                backgroundColor: gray[200]
                            }
                        }
                    },
                    {
                        props: {
                            color: 'secondary',
                            variant: 'text'
                        },
                        style: {
                            color: gray[600],
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: gray[100]
                            },
                            '&:active': {
                                backgroundColor: gray[200]
                            }
                        }
                    },
                    {
                        props: {
                            color: 'primary',
                            variant: 'text'
                        },
                        style: {
                            color: gray[900],
                            '&:hover': {
                                backgroundColor: gray[100]
                            },
                            '&:active': {
                                backgroundColor: gray[200]
                            }
                        }
                    },
                    {
                        props: {
                            color: 'success',
                            variant: 'text'
                        },
                        style: {
                            color: green[700],
                            '&:hover': {
                                backgroundColor: alpha(green[100], 0.6)
                            },
                            '&:active': {
                                backgroundColor: alpha(green[200], 0.6)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'danger',
                            variant: 'text'
                        },
                        style: {
                            color: dangerRed,
                            '&:hover': {
                                backgroundColor: alpha(dangerRed, 0.08)
                            },
                            '&:active': {
                                backgroundColor: alpha(dangerRed, 0.15)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'error',
                            variant: 'text'
                        },
                        style: {
                            color: dangerRed,
                            '&:hover': {
                                backgroundColor: alpha(dangerRed, 0.08)
                            },
                            '&:active': {
                                backgroundColor: alpha(dangerRed, 0.15)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'warning',
                            variant: 'text'
                        },
                        style: {
                            color: warningOrange.text,
                            '&:hover': {
                                backgroundColor: alpha(warningOrange.fill, 0.1)
                            },
                            '&:active': {
                                backgroundColor: alpha(warningOrange.fill, 0.18)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'info',
                            variant: 'text'
                        },
                        style: {
                            color: infoBlue,
                            '&:hover': {
                                backgroundColor: alpha(infoBlue, 0.08)
                            },
                            '&:active': {
                                backgroundColor: alpha(infoBlue, 0.15)
                            }
                        }
                    },
                    {
                        props: {
                            color: 'warning',
                            variant: 'outlined'
                        },
                        style: {
                            // No separate outlined warning look: it renders like the
                            // contained one (see warningOrange in themePrimitives).
                            color: warningOrange.onFill,
                            backgroundColor: warningOrange.fill,
                            borderColor: warningOrange.fill,
                            '&:hover': {
                                backgroundColor: warningOrange.fillHover,
                                borderColor: warningOrange.fillHover,
                                boxShadow: 'none'
                            },
                            '&:active': {
                                backgroundColor: warningOrange.fillHover,
                                borderColor: warningOrange.fillHover,
                                backgroundImage: 'none'
                            }
                        }
                    },
                    {
                        props: {
                            size: 'small'
                        },
                        style: {
                            height: '2rem',
                            padding: '8px',
                            minWidth: '50px'
                        }
                    },
                    {
                        props: {
                            size: 'medium'
                        },
                        style: {
                            minHeight: CONTROL_HEIGHT,
                            padding: '7px 12px'
                        }
                    },
                    {
                        props: {
                            size: 'large'
                        },
                        style: {
                            padding: '8px 12px'
                        }
                    }
                ]
            })
        }
    },
    MuiIconButton: {
        styleOverrides: {
            root: ({theme}) => ({
                boxShadow: 'none',
                borderRadius: 100,
                textTransform: 'none',
                fontWeight: theme.typography.fontWeightMedium,
                letterSpacing: 0,
                color: (theme.vars || theme).palette.text.primary,
                // border: '1px solid ',
                // borderColor: gray[200],
                backgroundColor: alpha(gray[50], 0.7),
                '.MuiInputBase-root &, .MuiDataGrid-treeDataGroupingCell &': {
                    backgroundColor: 'transparent'
                },
                '&:hover': {
                    backgroundColor: gray[100],
                    borderColor: gray[300]
                },
                '&:active': {
                    backgroundColor: gray[200]
                },
                // Inside DataGrid action cells the row may be tinted (success/warning/danger/info).
                // The opaque gray background clashes with those tints, so use a transparent base and
                // a low-alpha black overlay for hover/active — adapts to whatever row color is behind.
                '.MuiDataGrid-actionsCell &': {
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: alpha('#000', 0.06),
                        borderColor: 'transparent'
                    },
                    '&:active': {
                        backgroundColor: alpha('#000', 0.12)
                    }
                },
                '.MuiInputAdornment-root &': {
                    minHeight: 'unset'
                },
                variants: [
                    {
                        props: {
                            size: 'small'
                        },
                        style: {
                            width: '1.5rem',
                            height: '1.5rem',
                            [`& .${svgIconClasses.root}`]: {fontSize: '1rem'}
                        }
                    },
                    {
                        props: {
                            size: 'medium'
                        },
                        style: {
                            width: '2.2rem',
                            height: '2.2rem'
                        }
                    }
                ]
            })
        }
    },
    MuiToggleButtonGroup: {
        styleOverrides: {
            root: ({theme, ownerState}) => ({
                padding: TOGGLE_GROUP_PADDING,
                gap: 4,
                borderRadius: theme.shape.borderRadius + 4,
                backgroundColor: gray[100],
                [`&.MuiToggleButtonGroup-horizontal .${toggleButtonClasses.root}:not(:first-of-type)`]: {
                    marginLeft: 0
                },
                [`&.MuiToggleButtonGroup-vertical .${toggleButtonClasses.root}:not(:first-of-type)`]: {
                    marginTop: 0
                },
                [`& .${toggleButtonClasses.root}.${toggleButtonClasses.selected}`]: {
                    zIndex: 1
                },
                ...(ownerState?.exclusive ? {
                    '&.MuiToggleButtonGroup-horizontal': {
                        position: 'relative',
                        display: 'inline-grid',
                        gridAutoFlow: 'column',
                        gridAutoColumns: 'minmax(0, 1fr)',
                        alignItems: 'stretch',
                        isolation: 'isolate',
                        overflow: 'hidden'
                    },
                    '&.MuiToggleButtonGroup-horizontal.MuiToggleButtonGroup-fullWidth': {
                        width: '100%'
                    },
                    [`&.MuiToggleButtonGroup-horizontal > .${toggleButtonClasses.root}`]: {
                        position: 'relative',
                        zIndex: 1,
                        minWidth: 0
                    },
                    [`&.MuiToggleButtonGroup-horizontal:has(> .${toggleButtonClasses.root}.${toggleButtonClasses.selected})::before`]: segmentedControlIndicatorStyles(theme),
                    [`&.MuiToggleButtonGroup-horizontal:has(> .${toggleButtonClasses.root}.${toggleButtonClasses.selected}) > .${toggleButtonClasses.root}.${toggleButtonClasses.selected}`]: {
                        backgroundColor: 'transparent !important',
                        boxShadow: 'none !important',
                        '&:hover': {
                            backgroundColor: 'transparent !important'
                        }
                    },
                    ...segmentedControlItemCountStyles('&.MuiToggleButtonGroup-horizontal', `.${toggleButtonClasses.root}`),
                    ...segmentedControlSelectedIndexStyles('&.MuiToggleButtonGroup-horizontal', `.${toggleButtonClasses.root}`, `.${toggleButtonClasses.selected}`)
                } : {})
            })
        }
    },
    MuiToggleButton: {
        styleOverrides: {
            root: ({theme}) => ({
                textTransform: 'none',
                fontWeight: 600,
                // Keep labels on a single line and clip with an ellipsis when a group
                // is squeezed narrower than its content, rather than wrapping the text
                // mid-label (which looks broken). Ellipsis needs a block box + min-width:0
                // so the button can shrink below its content width.
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                minWidth: 0,
                // Icon-only toggles (grid/list view switchers etc.) have no text to
                // ellipsize — keep them flex-centered so the icon stays put.
                '&:has(> svg:only-child)': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                color: (theme.vars || theme).palette.text.secondary,
                border: '1px solid transparent',
                borderRadius: `${theme.shape.borderRadius}px !important`,
                backgroundColor: 'transparent',
                transition: 'all 100ms ease-in',
                '&:hover': {
                    color: (theme.vars || theme).palette.text.primary,
                    backgroundColor: gray[200],
                    borderColor: 'transparent',
                    '& .MuiChip-filledDefault': {
                        backgroundColor: gray[300]
                    }
                },
                [`&.${toggleButtonClasses.selected}`]: {
                    color: (theme.vars || theme).palette.text.primary,
                    backgroundColor: (theme.vars || theme).palette.background.paper,
                    borderColor: 'transparent',
                    boxShadow: segmentedControlShadow,
                    fontWeight: 700,
                    '&:hover': {
                        backgroundColor: (theme.vars || theme).palette.background.paper,
                        borderColor: 'transparent'
                    }
                },
                '&.Mui-disabled': {
                    color: (theme.vars || theme).palette.text.disabled,
                    borderColor: 'transparent'
                },
                variants: [
                    {
                        props: {
                            size: 'small'
                        },
                        style: {
                            padding: '3px 9px',
                            // Sized so group padding + button = CONTROL_HEIGHT.
                            minHeight: TOGGLE_BUTTON_HEIGHT,
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.125rem'
                            }
                        }
                    },
                    {
                        props: {
                            size: 'medium'
                        },
                        style: {
                            minHeight: '2.5rem',
                            padding: '8px 12px'
                        }
                    },
                    {
                        props: {
                            size: 'large'
                        },
                        style: {
                            padding: '12px 16px'
                        }
                    },
                    // Keep colour variants in the segmented style while tinting active text.
                    {
                        props: {color: 'secondary'},
                        style: {
                            [`&.${toggleButtonClasses.selected}`]: {
                                color: (theme.vars || theme).palette.text.primary,
                                backgroundColor: (theme.vars || theme).palette.background.paper,
                                borderColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: (theme.vars || theme).palette.background.paper,
                                    borderColor: 'transparent'
                                }
                            }
                        }
                    },
                    {
                        props: {color: 'success'},
                        style: {
                            [`&.${toggleButtonClasses.selected}`]: {
                                color: green[500],
                                backgroundColor: (theme.vars || theme).palette.background.paper,
                                borderColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: (theme.vars || theme).palette.background.paper,
                                    borderColor: 'transparent'
                                }
                            }
                        }
                    },
                    {
                        props: {color: 'danger'},
                        style: {
                            [`&.${toggleButtonClasses.selected}`]: {
                                color: dangerRed,
                                backgroundColor: (theme.vars || theme).palette.background.paper,
                                borderColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: (theme.vars || theme).palette.background.paper,
                                    borderColor: 'transparent'
                                }
                            }
                        }
                    },
                    {
                        props: {color: 'error'},
                        style: {
                            [`&.${toggleButtonClasses.selected}`]: {
                                color: dangerRed,
                                backgroundColor: (theme.vars || theme).palette.background.paper,
                                borderColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: (theme.vars || theme).palette.background.paper,
                                    borderColor: 'transparent'
                                }
                            }
                        }
                    },
                    {
                        props: {color: 'warning'},
                        style: {
                            [`&.${toggleButtonClasses.selected}`]: {
                                color: (theme.vars || theme).palette.warning.contrastText,
                                backgroundColor: (theme.vars || theme).palette.background.paper,
                                borderColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: (theme.vars || theme).palette.background.paper,
                                    borderColor: 'transparent'
                                }
                            }
                        }
                    },
                    {
                        props: {color: 'info'},
                        style: {
                            [`&.${toggleButtonClasses.selected}`]: {
                                color: infoBlue,
                                backgroundColor: (theme.vars || theme).palette.background.paper,
                                borderColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: (theme.vars || theme).palette.background.paper,
                                    borderColor: 'transparent'
                                }
                            }
                        }
                    }
                ]
            })
        }
    },
    MuiButtonGroup: {
        styleOverrides: {
            root: ({theme}) => ({
                boxShadow: 'none',
                borderRadius: theme.shape.borderRadius + 4,
                '& .MuiButton-root': {
                    marginTop: 0
                },

                '&.MuiButtonGroup-outlined': {
                    gap: 4,
                    padding: 4,
                    backgroundColor: gray[100],
                    borderRadius: theme.shape.borderRadius + 4
                },
                '&.MuiButtonGroup-outlined .MuiButton-root': {
                    border: '1px solid transparent !important',
                    borderRadius: `${theme.shape.borderRadius}px !important`,
                    color: (theme.vars || theme).palette.text.secondary,
                    backgroundColor: 'transparent',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': {
                        color: (theme.vars || theme).palette.text.primary,
                        backgroundColor: gray[200],
                        borderColor: 'transparent !important'
                    }
                },
                '&.MuiButtonGroup-outlined .MuiButton-contained': {
                    color: `${(theme.vars || theme).palette.text.primary} !important`,
                    backgroundColor: `${(theme.vars || theme).palette.background.paper} !important`,
                    borderColor: 'transparent !important',
                    boxShadow: `${segmentedControlShadow} !important`,
                    fontWeight: 700,
                    '&:hover': {
                        backgroundColor: `${(theme.vars || theme).palette.background.paper} !important`,
                        borderColor: 'transparent !important',
                        boxShadow: `${segmentedControlShadow} !important`
                    }
                },
                '&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal:has(> .MuiButton-contained)': {
                    position: 'relative',
                    display: 'inline-grid',
                    gridAutoFlow: 'column',
                    gridAutoColumns: 'minmax(0, 1fr)',
                    alignItems: 'stretch',
                    isolation: 'isolate',
                    overflow: 'hidden'
                },
                '&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal.MuiButtonGroup-fullWidth:has(> .MuiButton-contained)': {
                    width: '100%'
                },
                '&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal:has(> .MuiButton-contained) > .MuiButton-root': {
                    position: 'relative',
                    zIndex: 1,
                    minWidth: 0
                },
                '&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal:has(> .MuiButton-contained)::before': segmentedControlIndicatorStyles(theme),
                '&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal:has(> .MuiButton-contained) > .MuiButton-contained': {
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important',
                    '&:hover': {
                        backgroundColor: 'transparent !important',
                        boxShadow: 'none !important'
                    }
                },
                ...segmentedControlItemCountStyles('&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal', '.MuiButton-root'),
                ...segmentedControlSelectedIndexStyles('&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal', '.MuiButton-root', '.MuiButton-contained'),

                // Outlined groups render as segmented rails, so adjacent buttons should not
                // overlap or draw divider borders.
                '&.MuiButtonGroup-outlined.MuiButtonGroup-horizontal .MuiButton-root:not(:first-of-type)': {
                    marginLeft: 0
                },
                '&.MuiButtonGroup-outlined.MuiButtonGroup-vertical .MuiButton-root:not(:first-of-type)': {
                    marginTop: 0
                },

                // Contained: each contained color sets `border: 1px solid <color>` AND MUI's
                // own ButtonGroup applies a per-color `borderColor: palette[color].dark` rule
                // to first+middle children (only) — which lands as a hashed variant class in
                // emotion's output that wins the cascade against theme styleOverrides. The
                // result: first+middle buttons show a darker outline than the last button.
                // Strip the colored borders on every contained-group button — the bg color
                // defines the group perimeter, the boxShadow rules below define the dividers.
                // `!important` is required to beat MUI's variant-class specificity.
                '&.MuiButtonGroup-contained .MuiButton-root': {
                    borderColor: 'transparent !important'
                },
                // Every button reserves a `1px solid transparent` border for height parity
                // (see MuiButton.root). Contained groups draw their seams with box-shadow, not
                // borders, so those reserved borders must overlap by 1px — otherwise two
                // adjacent transparent edges sit side by side and read as a doubled divider.
                '&.MuiButtonGroup-contained.MuiButtonGroup-horizontal .MuiButton-root:not(:first-of-type)': {
                    marginLeft: -1
                },
                '&.MuiButtonGroup-contained.MuiButtonGroup-vertical .MuiButton-root:not(:first-of-type)': {
                    marginTop: -1
                },
                // Dividers between buttons (boxShadow can't be clobbered by MUI's border cascade).
                // `!important` is required because every contained Button variant sets
                // `boxShadow: 'none'` on hover, which would otherwise wipe the divider when any
                // button in the group is hovered.
                '&.MuiButtonGroup-contained.MuiButtonGroup-horizontal .MuiButton-root:not(:last-of-type)': {
                    boxShadow: `inset -1px 0 0 0 ${alpha('#000', 0.18)} !important`
                },
                '&.MuiButtonGroup-contained.MuiButtonGroup-vertical .MuiButton-root:not(:last-of-type)': {
                    boxShadow: `inset 0 -1px 0 0 ${alpha('#000', 0.18)} !important`
                },
                // Primary contained has a near-black bg; a dark divider is invisible. Use a light one.
                '&.MuiButtonGroup-contained.MuiButtonGroup-colorPrimary.MuiButtonGroup-horizontal .MuiButton-root:not(:last-of-type)': {
                    boxShadow: `inset -1px 0 0 0 ${alpha('#fff', 0.18)} !important`
                },
                '&.MuiButtonGroup-contained.MuiButtonGroup-colorPrimary.MuiButtonGroup-vertical .MuiButton-root:not(:last-of-type)': {
                    boxShadow: `inset 0 -1px 0 0 ${alpha('#fff', 0.18)} !important`
                },

                // Text: no borders at all — add an explicit divider line
                '&.MuiButtonGroup-text.MuiButtonGroup-horizontal .MuiButton-root:not(:last-of-type)': {
                    borderRight: `1px solid ${(theme.vars || theme).palette.divider}`
                },
                '&.MuiButtonGroup-text.MuiButtonGroup-vertical .MuiButton-root:not(:last-of-type)': {
                    borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`
                },

                // Allow outlined buttons inside contained groups to keep their styling
                '&.MuiButtonGroup-contained .MuiButton-outlined, &.MuiButtonGroup-contained .MuiButton-outlinedPrimary': {
                    backgroundColor: alpha(gray[50], 0.3),
                    color: gray[900],
                    borderColor: gray[200],
                    '&:hover': {
                        backgroundColor: gray[100],
                        borderColor: gray[300]
                    },
                    '&.Mui-disabled': {
                        backgroundColor: alpha(gray[50], 0.3),
                        color: alpha(gray[900], 0.4),
                        borderColor: alpha(gray[200], 0.5)
                    }
                }
            })
        }
    },
    MuiFormControlLabel: {
        styleOverrides: {
            root: () => ({
                marginLeft: 0,
                '&.MuiFormControlLabel-labelPlacementEnd': {
                    marginRight: 0
                }
            })
        }
    },
    MuiCheckbox: {
        defaultProps: {
            disableRipple: true,
            icon: (
                <CheckBoxOutlineBlankRoundedIcon sx={{color: 'hsla(210, 0%, 0%, 0.0)'}} />
            ),
            checkedIcon: <CheckRoundedIcon sx={{height: 14, width: 14}} />,
            indeterminateIcon: <RemoveRoundedIcon sx={{height: 14, width: 14}} />
        },
        styleOverrides: {
            root: ({theme}) => ({
                margin: 10,
                height: 16,
                width: 16,
                borderRadius: 5,
                border: '1px solid ',
                borderColor: alpha(gray[300], 0.8),
                boxShadow: '0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset',
                backgroundColor: alpha(gray[100], 0.4),
                transition: 'border-color, background-color, 120ms ease-in',
                '&:hover': {
                    borderColor: brand[300]
                },
                '&.Mui-focusVisible': {
                    outline: `3px solid ${alpha(brand[500], 0.5)}`,
                    outlineOffset: '2px',
                    borderColor: brand[400]
                },
                '&.Mui-checked:not(.Mui-disabled)': {
                    color: 'white',
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: theme.palette.primary.dark
                    }
                },
                variants: [
                    {
                        props: {color: 'warning'},
                        style: {
                            borderColor: warningOrange.border,
                            backgroundColor: alpha(warningOrange.surface, 0.6),
                            '&:hover': {
                                borderColor: warningOrange.fill
                            },
                            '&.Mui-checked:not(.Mui-disabled)': {
                                color: warningOrange.onFill,
                                backgroundColor: warningOrange.fill,
                                borderColor: warningOrange.fill,
                                '&:hover': {
                                    backgroundColor: warningOrange.fillHover
                                }
                            }
                        }
                    },
                    {
                        props: {color: 'success'},
                        style: {
                            '&:hover': {
                                borderColor: green[400]
                            },
                            '&.Mui-checked:not(.Mui-disabled)': {
                                backgroundColor: green[400],
                                borderColor: green[400],
                                '&:hover': {
                                    backgroundColor: green[500]
                                }
                            }
                        }
                    }
                ]
            })
        }
    },
    MuiInputBase: {
        styleOverrides: {
            root: () => ({
                background: '#fff',
                minHeight: CONTROL_HEIGHT,
                '& .MuiIconButton-edgeEnd': {
                    marginRight: -8
                },
                '.MuiDataGrid-cell &': {
                    // An inline editor is sized by its ROW, not by the filter bar;
                    // a compact grid row is shorter than 40px and the input would
                    // overflow it.
                    minHeight: 36,
                    paddingLeft: 8,
                    backgroundColor: alpha(gray[50], 0.7),
                    borderRadius: 5
                },
                '&:before': {
                    borderBottomColor: 'transparent !important'
                },
                // iOS Safari auto-zooms when a focused input has a computed
                // font-size < 16px and never restores the prior zoom on blur,
                // leaving sticky bars/dialogs offset. The compound selector
                // beats MUI's own `.MuiInputBase-input { font: inherit }`.
                '@media (pointer: coarse)': {
                    '& input, & textarea, & select': {
                        fontSize: 16
                    }
                },
                variants: [{
                    props: {size: 'medium'},
                    style: {
                        minHeight: CONTROL_HEIGHT_MEDIUM
                    }
                }]
            })
        }
    },
    MuiInputLabel: {
        styleOverrides: {
            root: ({theme}) => ({
                variants: [
                    {
                        // Un-shrunk only — a shrunk label belongs up on the notch,
                        // and MUI's own transform for that must survive.
                        props: ({ownerState}) => ownerState.variant === 'outlined'
                            && ownerState.size === 'small'
                            && !ownerState.shrink,
                        style: {
                            transform: `translate(14px, ${restingLabelOffset(theme, CONTROL_HEIGHT)}px) scale(1)`
                        }
                    },
                    {
                        props: ({ownerState}) => ownerState.variant === 'outlined'
                            && ownerState.size !== 'small'
                            && !ownerState.shrink,
                        style: {
                            transform: `translate(14px, ${restingLabelOffset(theme, CONTROL_HEIGHT_MEDIUM)}px) scale(1)`
                        }
                    }
                ]
            })
        }
    },
    MuiInput: {
        styleOverrides: {
            root: ({theme}) => ({
                border: `1px solid ${(theme.vars || theme).palette.divider}`,
                overflow: 'hidden',
                borderRadius: 4,
                input: {
                    padding: '1px 5px'
                }
            })
        }
    },
    MuiFilledInput: {
        styleOverrides: {
            root: ({theme}) => ({
                backgroundColor: alpha(gray[100], 0.7),
                borderRadius: (theme.vars || theme).shape.borderRadius,
                overflow: 'hidden',
                '&:hover': {
                    backgroundColor: alpha(gray[200], 0.3)
                },
                '&.Mui-focused': {
                    backgroundColor: alpha(gray[200], 0.5)
                },
                '&.MuiInputBase-readOnly': {
                    pointerEvents: 'none',
                    backgroundColor: alpha(gray[100], 0.7),
                    // Lay out the readOnly input as a flex column so the inner
                    // input grows to fill any extra vertical height handed to
                    // it by a stretching parent (e.g. STRETCH_SX in the gear
                    // item drawer when a sibling field is taller).
                    // Skip when the TextField sits inside an Autocomplete —
                    // the autocomplete wraps the input in its own structure
                    // and the flex-column treatment breaks its layout
                    // (chip/tag rows, popup anchor).
                    // Also skip when the input has an adornment (e.g. a "psi"
                    // unit): column direction stacks the adornment below the
                    // value and centers it; the default row layout keeps it
                    // inline to the right.
                    '.MuiTextField-root:not(.MuiAutocomplete-root *) &:not(:has(.MuiInputAdornment-root))': {
                        display: 'flex',
                        flexDirection: 'column',
                        '& input.MuiInputBase-input': {
                            flex: 1,
                            // The <input> defaults to box-sizing: content-box,
                            // so flex-stretching it to the container's width
                            // pushes its 12px L/R padding *outside* the root —
                            // the text then renders 12px left of the label.
                            // border-box keeps the padding inside the flex
                            // measurement so input edges hug the root.
                            boxSizing: 'border-box'
                        }
                    },
                    // Reserve room on the right when the readOnly input sits
                    // inside an Autocomplete that still renders its popup
                    // icon — the icon overlaps the value text otherwise.
                    '.MuiAutocomplete-hasPopupIcon &': {
                        paddingRight: theme.spacing(1.5)
                    },
                    '&:before, &:after': {
                        display: 'none'
                    },
                    '&.Mui-focused': {
                        backgroundColor: alpha(gray[100], 0.7)
                    },
                    '& .MuiChip-root': {
                        backgroundColor: 'white',
                        border: 0,
                        height: 23,
                        padding: 0,
                        '& .MuiChip-label': {
                            paddingLeft: 10,
                            paddingRight: 10
                        }
                    }
                },
                '.MuiAutocomplete-root &': {
                    paddingTop: 24,
                    paddingBottom: 5
                }
            })
        }
    },
    MuiSelect: {
        styleOverrides: {
            root: () => ({
                '&.MuiInputBase-readOnly .MuiSelect-icon': {
                    display: 'none'
                }
            })
        }
    },
    MuiTextField: {
        styleOverrides: {
            root: () => ({
                '&:has(.MuiInputBase-readOnly) > .MuiInputLabel-outlined': {
                    color: 'var(--template-palette-text-secondary)'
                },
                '&:has(.MuiInputBase-readOnly) > .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--template-palette-text-secondary)'
                },
                '& input[data-empty-readonly="true"], & textarea[data-empty-readonly="true"]': {
                    color: 'var(--template-palette-text-secondary)',
                    WebkitTextFillColor: 'var(--template-palette-text-secondary)'
                }
            })
        }
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: () => ({
                '&.MuiInputBase-readOnly input': {
                    cursor: 'default'
                },
                '&.MuiInputBase-readOnly .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                    borderColor: 'rgba(var(--template-palette-common-onBackgroundChannel) / 0.23)'
                }
            })
        }
    }
};
